/**
 * KAN-759: позиции и итоги заказа, общие для карточки заказа и модалок CRM.
 * С editor и правом permissions.edit: поиск товара каталога, количество,
 * удаление; без editor: только просмотр. Цена позиции приходит из каталога.
 * KAN-755: промокод (OrderPromoCode) в редактируемом заказе.
 */
import { AlertCircle } from 'lucide-react';
import type { OrderEditor } from '@/hooks/useOrderEditor';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { OrderDetail } from '@/types/order';
import { AddOrderProduct } from './AddOrderProduct';
import { OrderItemsTable } from './OrderItemsTable';
import { OrderPromoCode } from './OrderPromoCode';
import { OrderTotals } from './OrderTotals';
import { orderTotalsValues } from './orderFormat';

interface OrderItemsPanelProps {
  order: OrderDetail;
  /** Нет: только просмотр. */
  editor?: OrderEditor;
}

const noop = () => undefined;

export function OrderItemsPanel({ order, editor }: OrderItemsPanelProps) {
  const editable = !!editor && order.permissions.edit;
  const pending = editor?.pending ?? false;

  return (
    <div className="space-y-3">
      {editable && editor && (
        <div className="px-4">
          <AddOrderProduct pending={pending} onAdd={editor.add} />
        </div>
      )}
      {order.items.length > 0 ? (
        <OrderItemsTable
          items={order.items}
          editable={editable}
          pending={pending}
          onSetQuantity={editor?.setQuantity ?? noop}
          onRemove={editor?.remove ?? noop}
        />
      ) : (
        <p data-testid="order-empty" className="px-4 py-8 text-center text-sm text-gray-500">
          В заказе пока нет товаров.
        </p>
      )}
      {editable && editor && (
        <div className="px-4">
          <OrderPromoCode
            promoCode={order.promo_code}
            promoStatus={order.promo_status}
            pending={pending}
            error={editor.promoError}
            onApply={editor.applyPromo}
            onRemove={editor.removePromo}
          />
        </div>
      )}
      <OrderTotals {...orderTotalsValues(order)} />
    </div>
  );
}

/** Ошибка правки или перехода заказа текстом. */
export function OrderErrorAlert({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div
      role="alert"
      data-testid="order-error"
      className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      {extractErrorMessage(error, 'Не удалось сохранить изменения заказа')}
    </div>
  );
}
